import { type AskPunttProjectFile } from "@mg/schemas/src/christo/askPunttProject";

import { type CampaignExtraDocument } from "../components/CampaignDocsPanel";

const OFFICE_EXT = /\.(docx?|xlsx?|pptx?|ppsx?|odt|ods|odp|odg|odf)$/i;

/** Derive a human-readable filename from an https/s3 source URL or storage key. */
export function displayFileNameFromSource(
  sourceOrKey?: string | null,
): string | undefined {
  if (!sourceOrKey?.trim()) return undefined;
  const trimmed = sourceOrKey.trim();
  try {
    const segment = new URL(trimmed).pathname.split("/").filter(Boolean).pop();
    if (segment) return decodeURIComponent(segment);
  } catch {
    // s3://… and bare keys are not valid URLs for the URL constructor.
  }
  const path = trimmed.split("?")[0].split("#")[0];
  const segment = path.split("/").filter(Boolean).pop();
  if (!segment) return undefined;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * Stable id for the same logical upload across office originals and samba's
 * converted PDF (mirrors `get_name_id` in samba: basename stem, minus "-context").
 */
export function referenceDocumentIdentity(sourceOrKey: string): string {
  const name = displayFileNameFromSource(sourceOrKey) ?? sourceOrKey.trim();
  const stem = name.split(/[?#]/)[0].replace(/\.[^.]+$/, "");
  return stem.replace(/-context$/i, "").toLowerCase();
}

function isPdfSource(sourceOrKey: string): boolean {
  const name = displayFileNameFromSource(sourceOrKey) ?? sourceOrKey;
  return /\.pdf$/i.test(name.split(/[?#]/)[0]);
}

function isOfficeSource(sourceOrKey: string): boolean {
  const name = displayFileNameFromSource(sourceOrKey) ?? sourceOrKey;
  return OFFICE_EXT.test(name.split(/[?#]/)[0]);
}

/** Same-path office→pdf URL (citation rewrite pattern in christo comments). */
function officeSiblingPdfSource(source: string): string | undefined {
  const name = displayFileNameFromSource(source) ?? source;
  if (!OFFICE_EXT.test(name.split(/[?#]/)[0])) return undefined;
  try {
    const url = new URL(source);
    url.pathname = url.pathname.replace(OFFICE_EXT, ".pdf");
    return url.toString();
  } catch {
    return source.replace(OFFICE_EXT, ".pdf");
  }
}

type ReviewListItem = {
  contextAssets?: TicketAsset[];
  reviewFiles?: TicketAsset[];
};

type TicketAsset = { source: string; name?: string; type?: string };

function collectReviewAssets(
  reviews: ReviewListItem[] | undefined,
): { asset: TicketAsset; kind: CampaignExtraDocument["kind"] }[] {
  const out: { asset: TicketAsset; kind: CampaignExtraDocument["kind"] }[] = [];
  for (const r of reviews ?? []) {
    for (const f of r.contextAssets ?? []) {
      if (f.source) out.push({ asset: f, kind: "context" });
    }
    for (const f of r.reviewFiles ?? []) {
      if (f.source) out.push({ asset: f, kind: "artwork" });
    }
  }
  return out;
}

function registerProjectFileSources(
  projectFiles: AskPunttProjectFile[] | undefined,
  exactSources: Set<string>,
  pdfIdentities: Set<string>,
) {
  for (const f of projectFiles ?? []) {
    for (const v of f.versions ?? []) {
      if (v.source) {
        exactSources.add(v.source);
        if (isPdfSource(v.source)) {
          pdfIdentities.add(referenceDocumentIdentity(v.source));
        }
        const sibling = officeSiblingPdfSource(v.source);
        if (sibling) exactSources.add(sibling);
      }
      if (v.key) {
        exactSources.add(v.key);
        if (isPdfSource(v.key)) {
          pdfIdentities.add(referenceDocumentIdentity(v.key));
        }
      }
    }
  }
}

function shouldSkipReferenceSource(
  source: string,
  exactSources: Set<string>,
  pdfIdentities: Set<string>,
): boolean {
  if (exactSources.has(source)) return true;
  const id = referenceDocumentIdentity(source);
  if (isOfficeSource(source) && pdfIdentities.has(id)) return true;
  const sibling = officeSiblingPdfSource(source);
  if (sibling && exactSources.has(sibling)) return true;
  return false;
}

/** Mirrors the Reviews panel gate in christo `listReviews` (inverse filter). */
export function isProjectFileUnderReview(file: AskPunttProjectFile): boolean {
  const latest =
    file.versions.find((v) => v.versionNumber === file.currentVersion) ??
    file.versions[file.versions.length - 1];
  if (!latest) return false;
  return (
    (!!latest.reviewStatus && latest.reviewStatus !== "not_started") ||
    !!latest.reviewTicketId
  );
}

/**
 * Artwork and context/reference assets from project tickets that are not already
 * represented as versioned project files. Collapses office originals when a
 * converted PDF exists for the same upload.
 */
export function buildReferenceExtraDocuments(
  projectFiles: AskPunttProjectFile[] | undefined,
  reviews: ReviewListItem[] | undefined,
): CampaignExtraDocument[] {
  const exactSources = new Set<string>();
  const pdfIdentities = new Set<string>();
  registerProjectFileSources(projectFiles, exactSources, pdfIdentities);

  const byIdentity = new Map<string, CampaignExtraDocument>();

  const upsert = (f: TicketAsset, kind: CampaignExtraDocument["kind"]) => {
    if (
      !f.source ||
      shouldSkipReferenceSource(f.source, exactSources, pdfIdentities)
    ) {
      return;
    }
    const doc: CampaignExtraDocument = {
      source: f.source,
      name: f.name ?? displayFileNameFromSource(f.source) ?? "File",
      type: f.type ?? "file",
      kind,
    };
    const id = referenceDocumentIdentity(f.source);
    const existing = byIdentity.get(id);
    if (!existing) {
      byIdentity.set(id, doc);
      if (isPdfSource(f.source)) pdfIdentities.add(id);
      return;
    }
    if (isPdfSource(f.source) && !isPdfSource(existing.source)) {
      byIdentity.set(id, doc);
      pdfIdentities.add(id);
    }
  };

  for (const { asset, kind } of collectReviewAssets(reviews)) {
    upsert(asset, kind);
  }

  return Array.from(byIdentity.values());
}

/**
 * Versioned project files for Reference Documents. Hides an office original when
 * the same upload already appears as a PDF on the project or on a review ticket.
 */
export function projectReferenceFiles(
  files: AskPunttProjectFile[] | undefined,
  reviews?: ReviewListItem[] | undefined,
): AskPunttProjectFile[] {
  const exactSources = new Set<string>();
  const pdfIdentities = new Set<string>();
  registerProjectFileSources(files, exactSources, pdfIdentities);

  for (const { asset } of collectReviewAssets(reviews)) {
    if (!asset.source) continue;
    if (isPdfSource(asset.source)) {
      pdfIdentities.add(referenceDocumentIdentity(asset.source));
    }
    const sibling = officeSiblingPdfSource(asset.source);
    if (sibling) exactSources.add(sibling);
    exactSources.add(asset.source);
  }

  return (files ?? []).filter((file) => {
    const latest =
      file.versions.find((v) => v.versionNumber === file.currentVersion) ??
      file.versions[file.versions.length - 1];
    const source = latest?.source ?? latest?.key;
    if (!source) return true;
    if (!isOfficeSource(source)) return true;
    return !pdfIdentities.has(referenceDocumentIdentity(source));
  });
}
